import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap, withLatestFrom } from 'rxjs/operators';
import { AppState } from 'src/app/reducers';
import { PreguntaSatisfaccionService } from 'src/app/services/preguntaSatisfaccion.service';
import { ToastService } from 'src/app/services/toast.service';
import { EncuestaSatisfaccion, EncuestaSatisfaccionProps } from 'src/common/encuesta';
import { AddEncuestaAccepted, AddEncuestaCancelled, AddEncuestaRequested, EncuestaPropsAlreadyLoaded, EncuestaPropsCancelled, EncuestaPropsLoaded, EncuestaPropsRequested, PreguntaSatisfaccionActionTypes, PreguntasCancelled, PreguntasLoaded, PreguntasRequested } from './pregunta-satisfaccion.actions';
import { preguntaSatisfaccionEncuestaProps } from './pregunta-satisfaccion.selectors';

@Injectable()
export class PreguntaSatisfaccionEffects {

	loadPreguntas$ = createEffect( () =>{
		return this.actions$.pipe(
			ofType<PreguntasRequested>( PreguntaSatisfaccionActionTypes.PreguntasRequested ),
			switchMap( action => {
				return this.service.findPreguntasSatisfaccionActivas()
					.pipe(
						catchError( error => of(null) )
					);
			}),
			map( preguntas => {
				let action: Action = null;
				if( preguntas == null ) {
					this.toastService.danger( "No se pudo obtener la encuesta de satisfacción del usuario" );
					action = new PreguntasCancelled();
				} else
					action = new PreguntasLoaded( preguntas );
				return action;
			})
		);
	});

	addEncuesta$ = createEffect( () => {
		return this.actions$.pipe(
			ofType<AddEncuestaRequested>( PreguntaSatisfaccionActionTypes.AddEncuestaRequested ),
			switchMap( action => {
				let encuesta: EncuestaSatisfaccion = action.payload;
				return this.service.addEncuestaSatisfaccion( encuesta.preguntaAbrev, encuesta.descripcion, encuesta.mostrar )
					.pipe(
						map( () => {
							return { encuesta, hadError: false };
						}),
						catchError( error => of( { encuesta, hadError: true } ) )
					);
			}),
			map( data => {
				let returnAction: Action = null;
				if( data.hadError ) {
					this.toastService.danger( "No se pudo registar la encuesta" );
					returnAction = new AddEncuestaCancelled();
				} else
					returnAction = new AddEncuestaAccepted( { mostrar: data.encuesta.mostrar } );
				return returnAction;
			})
		);
	});

	getEncuestaProps$ = createEffect( () => {
		return this.actions$.pipe(
			ofType<EncuestaPropsRequested>( PreguntaSatisfaccionActionTypes.EncuestaPropsRequested ),
			withLatestFrom( this.store.select( preguntaSatisfaccionEncuestaProps ) ),
			switchMap( ([ action, props ]) => {
				let observable: Observable<{ alreadyLoaded: boolean, props: EncuestaSatisfaccionProps }> = null;
				if( props )
					observable = of( { alreadyLoaded: true, props } );
				else
					observable = this.service.getEncuestaSatisfaccionProps()
						.pipe(
							map( props => {
								return { alreadyLoaded: false, props };
							}),
							catchError( error => of( { alreadyLoaded: false, props: null } ) )
						);
				return observable;
			}),
			map( (data: { alreadyLoaded: boolean, props: EncuestaSatisfaccionProps }) => {
				let action: Action = null;
				if( data == null || data.props == null ) {
					this.toastService.danger( "No se pudieron recuperar las propiedades de la encuesta" );
					action = new EncuestaPropsCancelled();
				} else if( data.alreadyLoaded )
					action = new EncuestaPropsAlreadyLoaded();
				else
					action = new EncuestaPropsLoaded( data.props );
				return action;
			})
		);
	});

	constructor( private actions$: Actions, private store: Store<AppState>, private service: PreguntaSatisfaccionService, private toastService: ToastService ) {
	}

}